import { EmployeeAttribute, Language } from './employee.interface';
import { Timezone } from '../gql/graphql';

export interface Survey {
  id: number;
  name: string;
  surveyStatusId: number;
  languageCodes: string[];
  companyId: number;
  statusId: number;
  surveyTypeId: number;
  sendingEmailAddressId: number;
  timezoneId: number;
  showResults: boolean;
  anonymousConversation: boolean;
  startTimeUTC: Date;
  endTimeUTC: Date;
  fromEmailName: string;
  hashCode: string;
  emailLanguages: Language[];
  languageIds: number[];
  recipientIds: number[];
  combineLanguages: boolean;
  namesPerLanguage: { surveyName: string; languageCode: string }[];
  isContinuous?: boolean;
}

export interface SurveyDict {
  [id: number]: Survey;
  surveyId?: number;
  title?: string;
  message?: string;
  status?: string;
}

export interface SurveyReminderLanguage {
  languageId: number;
  emailSubject?: string;
  emailBody?: string;
}

export interface CreateSurveyInput {
  companyId: number;
  surveyTypeId: number | null;
  name: string;
  languageCodes: string[];
  showResults: boolean;
  enableConversations: boolean;
  combineLanguages: boolean;
  emailLanguages?: Language[];
  isContinuous?: Boolean;
  lifecycleSurveyTypeId?: number;
  clientTimezone: string;
}

export interface UpdateSurveyStatusInput {
  companyId: number;
  surveyId: number;
  surveyStatusId: number;
  surveyTypeId: number;
}

export interface UpdateSurveyInput {
  surveyId: number;
  companyId: number;
  surveyTypeId: number | null;
  surveyStatusId?: number | null;
  name: string;
  languageCodes: string[];
  showResults: boolean;
  enableConversations: boolean;
  emailAddressId: number;
  senderName: string;
  timezoneId: number;
  startTimeUTC?: string;
  endTimeUTC?: string;
  emailLanguages: Language[];
  employeeIds: number[];
  isSaveSurvey: boolean;
  combineLanguages: boolean;
  disableEmailSending?: boolean;
  invitationExpiryIn: number | null;
}

export interface TestSurveyResult {
  isSuccess: boolean;
  title: string;
  message: string;
  status: string;
}

export interface Translations {
  [code: string]: { title: string; desc: string; name?: string };
}

export interface QuestionLogic {
  id: number;
  surveyId: number;
  mainSurveyElementId: number;
  affectedSurveyElementId: number;
  rating: number;
  isHigherThan: boolean;
  isLowerThan: boolean;
  isEqualsTo: boolean;
  surveyElementQuestionOptionsChoiceId: number;
}

export interface QuestionLogicDict {
  [id: number]: QuestionLogic;
  surveyId?: number;
  mainSurveyElementId: number;
}

export interface Condition {
  id: number;
  affectedId: number;
  targetOption: number | string;
}

export interface SurveyUpdateDetails {
  name: string;
  surveyTypeId?: number | null;
  languageCodes: string[];
  showResults: boolean;
  enableConversations: boolean;
  combineLanguages: boolean;
  surveyStatusId?: number | null;
  emailAddressId: number;
  senderName: string;
  timezoneId: number;
  startTimeUTC?: string;
  endTimeUTC?: string;
  emailLanguages: Language[];
  employeeIds: number[];
  isSaveSurvey?: boolean;
  disableEmailSending?: boolean;
  invitationExpiryIn: number | null;
}

export enum Severity {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export interface SurveyConfig {
  surveyId: number;
  timezoneId: number | null;
  startTime: string | null;
  expiryTime: string | null;
}

export interface Question {
  title: string;
  hasAnswer: boolean;
  hasComment: boolean;
  uniqueAnswers: string[];
  hasValidRatingAnswer: boolean;
}

export interface SurveyData {
  headers: string[];
  commentHeaders: string[];
  answerHeaders: string[];
  unsupportedHeaders: string[];
  questionsDict: { [title: string]: Question };
  duplicatedQuestionsDict: { [title: string]: Question };
  employeeIdsWithAnalysis: { [employeeId: string]: boolean };
}

export interface ValidateSurveyMetadataParams {
  surveyData: SurveyData;
  attributeNames: string[];
  surveyElements: any[];
  employeeIds: string[];
  companyEmployeeIdsInSurvey: string[];
}

export interface QuestionMetadata {
  questionId: number;
  questionType: string;
  title: string;
  allowComments: boolean;
}

export interface SurveySearchParam {
  startTimeUTC?: string | null;
  endTimeUTC?: string | null;
  name?: string | null;
  surveyTypeId?: number | null;
  surveyStatusIds?: number[] | null;
}

export interface SurveyReminder {
  id: number;
  surveyId: number;
  scheduledDateTime: Date;
  status: string;
  languages: SurveyReminderLanguage[];
  survey: Survey;
  timezone: Timezone;
}

export interface SurveyColumn {
  headerName: string;
  allowSort?: boolean;
  orderBy?: string;
  headerClassName?: string;
  renderHeader?: Function;
}

export interface Permissions {
  [key: string]: boolean;
}

export interface SurveyTypePermissions {
  [surveyType: string]: Permissions;
}

export enum Step {
  CONFIG = 'configure',
  QUESTIONS = 'questions',
  SCHEDULE = 'schedule',
  AUTOMATE = 'automate',
}

export interface MigratedSurvey {
  surveyStatusId: number;
  timezoneId: number;
  startTimeUTC: Date;
  endTimeUTC: Date;
}

export interface SurveyMigrationTask {
  id: number;
  importType: string;
  status: string;
  surveyId: number;
  companyId: number;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Category {
  id: number;
  name: string;
  isKeyFactor: boolean;
  translations: Translations;
}

export interface DistributionResult {
  unfavorablePercent: number;
  neutralPercent: number;
  favorablePercent: number;
}

export interface selectable {
  label: string;
  value: number;
}

export interface SurveyElement {
  id: number;
  title: string;
  order: number;
  orderIndex: number;
  typeName: string;
  maxLabel: string;
  minLabel: string;
  maxOptions: number;
  minOptions: number;
  iconUrl: string;
  iconName: string;
  options: selectable;
  translations: Translations;
  isLibraryQuestion: boolean;
  isBenchmarkedQuestion: boolean;
}

export interface AnalyzeResultType {
  id: number;
  title: string;
  category: Category;
  surveyElement: SurveyElement;
  scaleDistribution: DistributionResult;
  enpsDistribution: DistributionResult;
  scoreAverage: number;
  enpsScore: number;
  isAnonymized: boolean;
}

export interface AttributeValueResult {
  id: string;
  attributeValue: string;
  attributeValueLabel: string;
  recipientCount: number;
  respondentCount: number;
  respondentPercent: number;
  isAnonymized: boolean;
  scaleRespondentCount: number;
  scoreAverage: number;
  enpsScore: number;
  scaleDistribution: DistributionResult;
  enpsDistribution: DistributionResult;
}

export interface AttributeResult {
  id: number;
  employeeAttribute: EmployeeAttribute;
  attributeId: number;
  attributeValueResults: AttributeValueResult[];
  isHierarchyAttribute: boolean;
  maxScaleFavorable: number;
  minScaleFavorable: number;
  maxEnpsFavorable: number;
  minEnpsFavorable: number;
  maxScoreAverage: number;
  minScoreAverage: number;
  maxEnpsScore: number;
  minEnpsScore: number;
}

export interface Rating {
  rating: number;
  totalAnswered: number;
  numberOfComments: number;
  percentage: number;
}

interface Option {
  id: number;
  text: string;
  totalAnswered: number;
  numberOfComments: number;
  percentage: number;
}

interface CommentSentiments {
  numberOfPositiveComments: number;
  numberOfNeutralComments: number;
  numberOfNegativeComments: number;
  numberOfUnknownComments: number;
}

export interface SurveyQuestionResult {
  id: number;
  surveyElementId: number;
  type: string;
  text: string;
  totalAnswered: number;
  totalRespondents: number;
  numberOfComments: number;
  iconPath: string;
  minRating: number;
  maxRating: number;
  average: number;
  ratings: Rating[];
  options: Option[];
  aggregatedCommentSentiments: CommentSentiments | null;
}

interface OptionResponse {
  id: string;
  rating: number | null;
  choiceId: number | null;
  respondentPercent: number;
  totalRespondents: number;
}

export interface QuestionResponse {
  id: string;
  totalRespondents: number;
  totalRecipients: number;
  totalComments: number;
  surveyElement: number;
  scoreAverage: number;
  questionTypeName: string;
  countFavorable: number;
  countNeutral: number;
  countUnfavorable: number;
  optionResponses: OptionResponse[];
}

export interface IndividualResults {
  employees: object[] | [];
  attributes: object[] | [];
  scaleQuestions: object[] | [];
  textQuestions: object[] | [];
  optionsQuestions: object[] | [];
}

export interface BenchmarkRecord {
  id: number;
  benchmarkQuestionId: number;
  scope: string;
  regionValueId?: number | null;
  countryValueId?: number | null;
  sizeValueId?: number | null;
  industryValueId?: number | null;
  sectorValueId?: number | null;
  averageScore: number | null;
  positivePercent: number | null;
  neutralPercent: number | null;
  negativePercent: number | null;
  promoter: number | null;
  passive: number | null;
  detractor: number | null;
  eNPSScore: number | null;
  totalCount: number;
  companiesCount: number;
}

export interface BenchmarkResult {
  [surveyId: number]: {
    [benchmarkPeriod: string]: {
      [questionId: number]: BenchmarkRecord[];
    };
  };
}

export interface BenchmarkAttributes {
  regionValueId?: number;
  countryValueId?: number;
  sizeValueId?: number;
  industryValueId?: number;
  sectorValueId?: number;
}

export interface BenchmarkSelectOptions {
  surveyId: number;
  benchmarkPeriod: string;
  benchmarkOption: string;
  questionId?: number;
}

export interface CompareStatus {
  type: string;
  compareSurveyId: number | null;
  isAttributeCompare: boolean;
  compareAttributes: { id: number; name: string; values: unknown }[];
  title: null | string;
}

export interface DistributionResult {
  unfavorablePercent: number;
  neutralPercent: number;
  favorablePercent: number;
}

export interface BenchmarkStats {
  averageScore: number;
  positivePercent: number;
  neutralPercent: number;
  negativePercent: number;
  totalCount: number;
  companiesCount: number;
}

export interface ScaleStats {
  unfavorablePercent: number;
  neutralPercent: number;
  favorablePercent: number;
  averageScore: number;
}

export interface SurveyReportAttributeValue {
  id: string;
  label?: string;
  selected?: boolean;
  count?: number;
  disabled?: boolean;
}

export interface SurveyReportAttribute {
  id: number;
  name: string;
  isEmpty?: boolean;
  values?: SurveyReportAttributeValue[];
}

export interface LegacySurveyReportContext {
  userCompanyData?: any;
  uniqueLanguageIds?: number[];
  uniqueCompanyIds: number[];
  activeFilter?: string[];
  attributeViewActiveFilter?: string[];
  currentSurveyId?: number;
  currentSurvey?: any;
  surveyTypeId: number;
  attributes?: SurveyReportAttribute[];
  activeAttributes?: { [key: string]: string[] };
  responseRatingsAttributes?: any;
  selectedAttribute?: any;
  aggregatedRatingResponses?: any[];
  visualizationFilterType?: string;
  algorithmFilterType?: string;
  selectedSurveys?: Survey[];
  selectedSurveyIdsList: number[];
  overallSpreadAttributes?: any;
  surveyTypeAttributes?: any;
  currentTotalRespondents?: number | null;
  mainTabIndex?: number;
  overallSurveyResults?: any;
  attributeResponses?: any;
  responseRateIndex?: number;
  selectedResponseRateAttribute?: any;
  resultsActiveQuestion?: any;
  resultsActiveQuestionTab?: number | null;
  resultsActiveComments?: string[];
  analyzeFactorsDetail?: any;
  analyzeAttributesDetail?: any;
  analyzeIndex?: number;
  activeHeatMapResponse?: any;
  activeHeatMapAttributes?: any;
  activeHeatMapDataShown?: string;
  commentsList?: string[];
  commentSearchText?: string;
  commentSentiments?: string[];
  activeResultSelectedAttribute?: any;
  factorTrendsData?: any[];
  questionTrendsData?: any[];
  selectedFactors?: any[];
  selectedQuestions?: any[];
  trendIndex?: number;
  selectedHeatmapAttribute?: any;
  surveyQuestionBriefs?: any;
  managerReportIds?: string[];
  isHierarchyReport?: boolean;
  requestManagerHierarchyData?: boolean;
  peopleLeaderId?: number;
  selectedAttributes?: SurveyReportAttribute[];
  selectedFactorIds?: number[];
  selectedFactor?: any;
  selectedFactorQuestion?: any;
  selectedAttributeView?: any;
  factorIndex?: number;
  keyFactorIndex?: number;
  selectedAttributesQueryInput?: any;
  surveyThreshold: number;
  attributesWithAllAnonymized: any[] | undefined;
}

export interface SurveyElementToExport {
  id: number;
  typeKey: string;
  totalOptions: number;
  translations: {
    [key: string]: {
      desc: string;
      languageId: number;
      maxLabel: string;
      minLabel: string;
      title: string;
    };
  };
  order: number;
  typeLabel: string;
  categoryName: string;
  showDescription: boolean;
  benchmarkQuestionTranslations: {
    languageId: number;
    questionText: string;
  }[];
  isRequired: boolean;
  allowComments: boolean;
  isPreviousQuestion: boolean;
  isAttributeBased: boolean;
  isBenchmarkedQuestion: boolean;
}

export interface EmailLanguage {
  languageCode: string;
  subject: string;
  content: string;
}
